import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import UserService from "rc-app/services/UserService";
import { useDesktopMode } from "rc-app/hooks/useDesktopMode";
import DesktopModePrompt from "rc-app/components/DesktopModePrompt";
import {
  getClientAdminByEmailId,
  getClientManagerByEmailId,
} from "rc-app/api/systemBusinessAdminActionApi";
import { getAgentProfile, getAllAgent } from "rc-app/api/agentActionsApi";
import { getClientProjects } from "rc-app/api/projectActionsApi";
import { getCustomerList } from "rc-app/api/customerActionsApi";
import {
  getBusinessAdminList,
  getSystemAdminList,
} from "rc-app/api/systemBusinessAdminActionApi";

const VR_SESSIONS_BASE_URL = process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL;

type VrSession = {
  _id: string;
  session_id: string;
  created_at: string;
  timestamp?: string;
  project_id: string | null;
  digital_twin_id: string | null;
  user_id: string | null;
  customer_id: string | null;
  agency_id: string | null;
  agent_id: string[] | null;
  city: string | null;
  region: string | null;
  country: string | null;
  device: string | null;
  total_duration: number | null;
  used_vr: boolean | null;
  vr_time: number | null;
  experiences_visited: string[] | null;
  most_viewed_zone: string | null;
  most_viewed_time: number | null;
  total_nodes: number | null;
  status: string | null;
  rating: number | null;
  [k: string]: unknown;
};

type ScopeIds = {
  agencyId?: string;
  agentId?: string;
  clientAdminId?: string;
  clientManagerId?: string;
};

const buildSessionsUrl = (scope: ScopeIds) => {
  const params = new URLSearchParams();
  if (scope.agencyId) params.set("agency_id", scope.agencyId);
  params.set("page", "0");
  params.set("limit", "10000");
  return `${VR_SESSIONS_BASE_URL}/api/v1/vr/sessions?${params.toString()}`;
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const joinName = (
  first: unknown,
  last: unknown,
): string => {
  const f = typeof first === "string" ? first.trim() : "";
  const l = typeof last === "string" ? last.trim() : "";
  return [f, l].filter(Boolean).join(" ");
};

const csvEscape = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadSessionsCsv = (
  rows: VrSession[],
  resolveUserProfile: (s: VrSession) => { kind: UserProfileKind; name: string | null },
  projectNameById: Map<string, string>,
) => {
  const headers = [
    "Session",
    "Date",
    "User Profile",
    "Project",
    "Device",
    "Location",
    "Duration (seconds)",
    "VR",
    "Most viewed zone",
    "Status",
  ];
  const lines: string[] = [headers.map(csvEscape).join(",")];
  for (const s of rows) {
    const { kind, name } = resolveUserProfile(s);
    const profile = name
      ? `${USER_PROFILE_LABEL[kind]} ${name}`
      : USER_PROFILE_LABEL[kind];
    const projectName =
      (s.project_id && projectNameById.get(s.project_id)) || s.project_id || "";
    const vr =
      s.used_vr === true ? "VR" : s.used_vr === false ? "2D" : "";
    lines.push(
      [
        s.session_id ?? "",
        s.created_at ?? "",
        profile,
        projectName,
        s.device ?? "",
        formatLocation(s),
        typeof s.total_duration === "number" ? s.total_duration : "",
        vr,
        s.most_viewed_zone ?? "",
        s.status ?? "",
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  const csv = "﻿" + lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vr-sessions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const DATE_PRESETS: { key: string; label: string; days: number | null }[] = [
  { key: "lifetime", label: "Lifetime", days: null },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "14", label: "Last 14 days", days: 14 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "60", label: "Last 60 days", days: 60 },
];

const computeDateRange = (days: number | null): { from: string; to: string } => {
  if (days === null) return { from: "", to: "" };
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};

const formatLocation = (s: VrSession): string => {
  const parts = [s.city, s.country].filter(Boolean) as string[];
  return parts.length ? parts.join(", ") : "—";
};

type TabKey = "overall" | "project";

type UserProfileKind =
  | "business_admin"
  | "system_admin"
  | "agent"
  | "customer"
  | "raw_visitor";

const USER_PROFILE_LABEL: Record<UserProfileKind, string> = {
  business_admin: "Business Admin",
  system_admin: "System Admin",
  agent: "Agent",
  customer: "Lead",
  raw_visitor: "Raw Visitor",
};

const USER_PROFILE_STYLE: Record<UserProfileKind, string> = {
  business_admin: "bg-amber-50 border border-amber-300 text-amber-800",
  system_admin: "bg-rose-50 border border-rose-300 text-rose-800",
  agent: "bg-sky-50 border border-sky-300 text-sky-800",
  customer: "bg-emerald-50 border border-emerald-300 text-emerald-800",
  raw_visitor: "bg-gray-50 border border-gray-200 text-gray-600",
};

const EXPERIENCE_PALETTE: string[] = [
  "bg-indigo-100 text-indigo-700",
  "bg-amber-100 text-amber-700",
  "bg-teal-100 text-teal-700",
  "bg-pink-100 text-pink-700",
  "bg-cyan-100 text-cyan-700",
  "bg-lime-100 text-lime-700",
  "bg-orange-100 text-orange-700",
  "bg-fuchsia-100 text-fuchsia-700",
];

const experienceColor = (name: string): string => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % EXPERIENCE_PALETTE.length;
  return EXPERIENCE_PALETTE[idx];
};

const ClientAnalyticsV2 = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { showPrompt, accept, dismiss } = useDesktopMode();
  const [activeTab, setActiveTab] = useState<TabKey>("overall");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const projectPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!projectPickerOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        projectPickerRef.current &&
        !projectPickerRef.current.contains(e.target as Node)
      ) {
        setProjectPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [projectPickerOpen]);

  const [scope, setScope] = useState<ScopeIds>({});
  const [scopeResolved, setScopeResolved] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datePreset, setDatePreset] = useState<string>("lifetime");

  const [sessions, setSessions] = useState<VrSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionsPage, setSessionsPage] = useState(1);
  const SESSIONS_PAGE_SIZE = 10;

  const [totalProjects, setTotalProjects] = useState<number | null>(null);
  const [projectNameById, setProjectNameById] = useState<Map<string, string>>(
    new Map(),
  );
  const [businessAdminById, setBusinessAdminById] = useState<
    Map<string, string>
  >(new Map());
  const [systemAdminById, setSystemAdminById] = useState<Map<string, string>>(
    new Map(),
  );
  const [agentNameById, setAgentNameById] = useState<Map<string, string>>(
    new Map(),
  );
  const [customerNameById, setCustomerNameById] = useState<Map<string, string>>(
    new Map(),
  );

  const [allBusinessAdmins, setAllBusinessAdmins] = useState<any[]>([]);
  const [allSystemAdmins, setAllSystemAdmins] = useState<any[]>([]);
  const [allAgents, setAllAgents] = useState<any[]>([]);

  const [allCustomers, setAllCustomers] = useState<any[]>([]);

  const [projectSubTab, setProjectSubTab] = useState<
    "user_profile" | "location" | "experience"
  >("user_profile");
  const [expandedRoles, setExpandedRoles] = useState<Set<UserProfileKind>>(
    new Set(),
  );
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [assignedLeads, setAssignedLeads] = useState<number | null>(null);
  const [unassignedLeads, setUnassignedLeads] = useState<number | null>(null);

  useEffect(() => {
    const email = UserService.getUseremail();

    if (UserService.hasRole("agency_business_admin")) {
      getClientManagerByEmailId(email)
        .then((res) => {
          setScope({
            agencyId: res.agency_business_admin?.agency_id,
            clientManagerId: res.agency_business_admin?._id,
          });
        })
        .catch((e) => setError(e?.message ?? "Failed to resolve manager"))
        .finally(() => setScopeResolved(true));
      return;
    }

    if (UserService.hasRole("agency_system_admin")) {
      getClientAdminByEmailId(email)
        .then((res) => {
          setScope({
            agencyId: res.agency_system_admin?.agency_id,
            clientAdminId: res.agency_system_admin?.id,
          });
        })
        .catch((e) => setError(e?.message ?? "Failed to resolve admin"))
        .finally(() => setScopeResolved(true));
      return;
    }

    if (UserService.hasRole("agent")) {
      getAgentProfile(email)
        .then((res) => {
          setScope({
            agencyId: res?.agent?.agency?.id,
            agentId: res?.agent?._id,
          });
        })
        .catch((e) => setError(e?.message ?? "Failed to resolve agent"))
        .finally(() => setScopeResolved(true));
      return;
    }

    setScopeResolved(true);
  }, []);

  const fetchSessions = async () => {
    if (!scope.agencyId) {
      setError("No agency context — cannot fetch sessions.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const url = buildSessionsUrl(scope);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(
          `Request failed: ${response.status} ${response.statusText}`,
        );
      }
      const data = await response.json();
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to fetch sessions");
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scopeResolved && scope.agencyId) {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeResolved, scope.agencyId]);

  useEffect(() => {
    let cancelled = false;
    if (scope.agencyId) {
      getClientProjects(scope.agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.projects)
          ? res.projects
          : Array.isArray(res)
          ? res
          : [];
        setTotalProjects(res?.total ?? list.length);
        const map = new Map<string, string>();
        for (const p of list) {
          const id = p?._id ?? p?.id;
          const name = p?.project_name ?? p?.name;
          if (id && name) map.set(String(id), String(name));
        }
        setProjectNameById(map);
      });

      getCustomerList(0, 10000, scope.agencyId)
        .then((res: any) => {
          if (cancelled) return;
          const list: any[] = Array.isArray(res?.re_customer_list)
            ? res.re_customer_list
            : Array.isArray(res?.customers)
            ? res.customers
            : Array.isArray(res)
            ? res
            : [];
          let assigned = 0;
          let unassigned = 0;
          const customerMap = new Map<string, string>();
          for (const c of list) {
            const aid = c?.agent_id;
            const hasAgent = Array.isArray(aid)
              ? aid.some((id) => typeof id === "string" && id.trim() !== "")
              : typeof aid === "string"
              ? aid.trim() !== ""
              : aid != null;
            if (hasAgent) assigned++;
            else unassigned++;
            const id = c?._id ?? c?.id;
            const name = joinName(c?.first_name, c?.last_name) || c?.name;
            if (id && name) customerMap.set(String(id), String(name));
          }
          setAssignedLeads(assigned);
          setUnassignedLeads(unassigned);
          setCustomerNameById(customerMap);
          setAllCustomers(list);
        })
        .catch(() => {
          if (cancelled) return;
          setAssignedLeads(null);
          setUnassignedLeads(null);
        });

      getAllAgent(0, 10000, scope.agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.re_agent_list)
          ? res.re_agent_list
          : Array.isArray(res)
          ? res
          : [];
        const map = new Map<string, string>();
        for (const a of list) {
          const id = a?._id ?? a?.id;
          const name = joinName(a?.first_name, a?.last_name) || a?.name;
          if (id && name) map.set(String(id), String(name));
        }
        setAgentNameById(map);
        setAllAgents(list);
      });

      getBusinessAdminList(scope.agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.agency_business_admin_list)
          ? res.agency_business_admin_list
          : [];
        const map = new Map<string, string>();
        for (const a of list) {
          const id =
            a?._id ?? a?.id ?? a?.agency_business_admin_id;
          const name = a?.name ?? joinName(a?.first_name, a?.last_name);
          if (id && name) map.set(String(id), String(name));
        }
        setBusinessAdminById(map);
        setAllBusinessAdmins(list);
      });

      getSystemAdminList(scope.agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.agency_system_admin_list)
          ? res.agency_system_admin_list
          : [];
        const map = new Map<string, string>();
        for (const a of list) {
          const id = a?._id ?? a?.id ?? a?.agency_system_admin_id;
          const name = a?.name ?? joinName(a?.first_name, a?.last_name);
          if (id && name) map.set(String(id), String(name));
        }
        setSystemAdminById(map);
        setAllSystemAdmins(list);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [scope.agencyId]);

  const isAgent = UserService.hasRole("agent");

  const filteredSessions = useMemo(() => {
    let result = sessions;
    if (isAgent && scope.agentId) {
      const agentId = scope.agentId;
      result = result.filter(
        (s) => Array.isArray(s.agent_id) && s.agent_id.includes(agentId),
      );
    }
    if (fromDate || toDate) {
      const start = fromDate
        ? new Date(fromDate + "T00:00:00").getTime()
        : -Infinity;
      const end = toDate
        ? new Date(toDate + "T23:59:59.999").getTime()
        : Infinity;
      result = result.filter((s) => {
        const t = s.updated_at ? new Date(s.updated_at as string).getTime() : 0;
        return t >= start && t <= end;
      });
    }
    return result;
  }, [sessions, fromDate, toDate, isAgent, scope.agentId]);

  const agentLeadCount = useMemo(() => {
    if (!isAgent || !scope.agentId) return 0;
    const agentId = scope.agentId;
    return allCustomers.filter(
      (c) =>
        Array.isArray(c?.agent_id) &&
        c.agent_id.some(
          (id: unknown) => typeof id === "string" && id === agentId,
        ),
    ).length;
  }, [allCustomers, isAgent, scope.agentId]);

  const projectSessions = useMemo(
    () =>
      selectedProjectId
        ? filteredSessions.filter((s) => s.project_id === selectedProjectId)
        : [],
    [filteredSessions, selectedProjectId],
  );

  const projectSummary = useMemo(() => {
    const totalViews = projectSessions.length;
    let mobileViews = 0;
    let desktopViews = 0;
    let vrViews = 0;
    let totalDuration = 0;
    for (const s of projectSessions) {
      if (s.device === "mobile") mobileViews++;
      else if (s.device === "desktop") desktopViews++;
      if (s.used_vr === true) vrViews++;
      if (typeof s.total_duration === "number")
        totalDuration += s.total_duration;
    }
    const avgDuration =
      totalViews > 0 ? Math.round(totalDuration / totalViews) : 0;
    return {
      totalViews,
      mobileViews,
      desktopViews,
      vrViews,
      totalDuration,
      avgDuration,
    };
  }, [projectSessions]);

  const projectUserBreakdown = useMemo(() => {
    type ExpStat = { views: number; timeSpent: number };
    type UserRow = {
      id: string;
      name: string;
      phone: string;
      views: number;
      timeSpent: number;
      experiences: Set<string>;
      experienceStats: Map<string, ExpStat>;
    };
    type Bucket = {
      views: number;
      timeSpent: number;
      experiences: Set<string>;
      experienceStats: Map<string, ExpStat>;
      users: Map<string, UserRow>;
    };
    const newBucket = (): Bucket => ({
      views: 0,
      timeSpent: 0,
      experiences: new Set<string>(),
      experienceStats: new Map<string, ExpStat>(),
      users: new Map<string, UserRow>(),
    });
    const buckets: Record<UserProfileKind, Bucket> = {
      business_admin: newBucket(),
      system_admin: newBucket(),
      agent: newBucket(),
      customer: newBucket(),
      raw_visitor: newBucket(),
    };

    const findRecord = (list: any[], id: string) =>
      list.find((r) => {
        const rid =
          r?._id ?? r?.id ?? r?.agency_business_admin_id ?? r?.agency_system_admin_id;
        return String(rid) === id;
      });

    const getPhone = (rec: any): string =>
      rec?.mobile_number ?? rec?.mobile ?? rec?.phone ?? rec?.phone_number ?? "—";

    const addToBucket = (b: Bucket, s: VrSession) => {
      b.views++;
      if (typeof s.total_duration === "number") b.timeSpent += s.total_duration;
      const expTimes =
        s.experience_times && typeof s.experience_times === "object"
          ? (s.experience_times as Record<string, number>)
          : {};
      if (Array.isArray(s.experiences_visited)) {
        for (const e of s.experiences_visited) {
          if (!e) continue;
          b.experiences.add(e);
          const stat = b.experienceStats.get(e) ?? {
            views: 0,
            timeSpent: 0,
          };
          stat.views++;
          const t = expTimes[e];
          if (typeof t === "number") stat.timeSpent += t;
          b.experienceStats.set(e, stat);
        }
      }
    };

    const upsertUser = (
      b: Bucket,
      id: string,
      name: string,
      phone: string,
      s: VrSession,
    ) => {
      let row = b.users.get(id);
      if (!row) {
        row = {
          id,
          name,
          phone,
          views: 0,
          timeSpent: 0,
          experiences: new Set<string>(),
          experienceStats: new Map<string, ExpStat>(),
        };
        b.users.set(id, row);
      }
      row.views++;
      if (typeof s.total_duration === "number")
        row.timeSpent += s.total_duration;
      const expTimes =
        s.experience_times && typeof s.experience_times === "object"
          ? (s.experience_times as Record<string, number>)
          : {};
      if (Array.isArray(s.experiences_visited)) {
        for (const e of s.experiences_visited) {
          if (!e) continue;
          row.experiences.add(e);
          const stat = row.experienceStats.get(e) ?? {
            views: 0,
            timeSpent: 0,
          };
          stat.views++;
          const t = expTimes[e];
          if (typeof t === "number") stat.timeSpent += t;
          row.experienceStats.set(e, stat);
        }
      }
    };

    const nonEmptyString = (v: unknown): string | null => {
      if (v === null || v === undefined) return null;
      if (typeof v === "string") return v.trim() !== "" ? v : null;
      if (typeof v === "object") {
        const oid = (v as any).$oid ?? (v as any)._id;
        if (typeof oid === "string" && oid.trim() !== "") return oid;
      }
      const s = String(v).trim();
      return s !== "" && s !== "[object Object]" ? s : null;
    };

    for (const s of projectSessions) {
      const abaId = nonEmptyString((s as any).aba_id);
      if (abaId) {
        addToBucket(buckets.business_admin, s);
        const rec = findRecord(allBusinessAdmins, abaId);
        upsertUser(
          buckets.business_admin,
          abaId,
          rec ? rec.name ?? joinName(rec.first_name, rec.last_name) : "—",
          rec ? getPhone(rec) : "—",
          s,
        );
        continue;
      }

      const asaId = nonEmptyString((s as any).asa_id);
      if (asaId) {
        addToBucket(buckets.system_admin, s);
        const rec = findRecord(allSystemAdmins, asaId);
        upsertUser(
          buckets.system_admin,
          asaId,
          rec ? rec.name ?? joinName(rec.first_name, rec.last_name) : "—",
          rec ? getPhone(rec) : "—",
          s,
        );
        continue;
      }

      const primaryAgent = Array.isArray(s.agent_id)
        ? s.agent_id.find(
            (x): x is string => typeof x === "string" && x.trim() !== "",
          )
        : null;
      if (primaryAgent) {
        addToBucket(buckets.agent, s);
        const rec = findRecord(allAgents, primaryAgent);
        upsertUser(
          buckets.agent,
          primaryAgent,
          rec ? joinName(rec.first_name, rec.last_name) || rec.name : "—",
          rec ? getPhone(rec) : "—",
          s,
        );
        continue;
      }

      const customerId = nonEmptyString(s.customer_id);
      if (customerId) {
        addToBucket(buckets.customer, s);
        const rec = findRecord(allCustomers, customerId);
        upsertUser(
          buckets.customer,
          customerId,
          rec ? joinName(rec.first_name, rec.last_name) || rec.name : "—",
          rec ? getPhone(rec) : "—",
          s,
        );
        continue;
      }

      addToBucket(buckets.raw_visitor, s);
    }

    return buckets;
  }, [
    projectSessions,
    allBusinessAdmins,
    allSystemAdmins,
    allAgents,
    allCustomers,
  ]);

  const projectLocationBreakdown = useMemo(() => {
    type CityRow = {
      city: string;
      views: number;
      timeSpent: number;
      businessAdminIds: Set<string>;
      systemAdminIds: Set<string>;
      agentIds: Set<string>;
      customerIds: Set<string>;
      rawVisitorViews: number;
    };
    const byCity = new Map<string, CityRow>();
    const nonEmptyString = (v: unknown): string | null => {
      if (v === null || v === undefined) return null;
      if (typeof v === "string") return v.trim() !== "" ? v : null;
      if (typeof v === "object") {
        const oid = (v as any).$oid ?? (v as any)._id;
        if (typeof oid === "string" && oid.trim() !== "") return oid;
      }
      const s = String(v).trim();
      return s !== "" && s !== "[object Object]" ? s : null;
    };

    for (const s of projectSessions) {
      const city =
        typeof s.city === "string" && s.city.trim() ? s.city : "Unknown";
      let row = byCity.get(city);
      if (!row) {
        row = {
          city,
          views: 0,
          timeSpent: 0,
          businessAdminIds: new Set<string>(),
          systemAdminIds: new Set<string>(),
          agentIds: new Set<string>(),
          customerIds: new Set<string>(),
          rawVisitorViews: 0,
        };
        byCity.set(city, row);
      }
      row.views++;
      if (typeof s.total_duration === "number")
        row.timeSpent += s.total_duration;

      const abaId = nonEmptyString((s as any).aba_id);
      if (abaId) {
        row.businessAdminIds.add(abaId);
        continue;
      }
      const asaId = nonEmptyString((s as any).asa_id);
      if (asaId) {
        row.systemAdminIds.add(asaId);
        continue;
      }
      const primaryAgent = Array.isArray(s.agent_id)
        ? s.agent_id.find(
            (x): x is string => typeof x === "string" && x.trim() !== "",
          )
        : null;
      if (primaryAgent) {
        row.agentIds.add(primaryAgent);
        continue;
      }
      const customerId = nonEmptyString(s.customer_id);
      if (customerId) {
        row.customerIds.add(customerId);
        continue;
      }
      row.rawVisitorViews++;
    }

    return Array.from(byCity.values()).sort((a, b) => b.views - a.views);
  }, [projectSessions]);

  const projectExperienceBreakdown = useMemo(() => {
    type ExpRow = {
      experience: string;
      views: number;
      timeSpent: number;
      businessAdminIds: Set<string>;
      systemAdminIds: Set<string>;
      agentIds: Set<string>;
      customerIds: Set<string>;
      rawVisitorViews: number;
    };
    const byExp = new Map<string, ExpRow>();
    const nonEmptyString = (v: unknown): string | null => {
      if (v === null || v === undefined) return null;
      if (typeof v === "string") return v.trim() !== "" ? v : null;
      if (typeof v === "object") {
        const oid = (v as any).$oid ?? (v as any)._id;
        if (typeof oid === "string" && oid.trim() !== "") return oid;
      }
      const s = String(v).trim();
      return s !== "" && s !== "[object Object]" ? s : null;
    };

    for (const s of projectSessions) {
      const visited = Array.isArray(s.experiences_visited)
        ? s.experiences_visited.filter(
            (e): e is string => typeof e === "string" && e.trim() !== "",
          )
        : [];
      if (visited.length === 0) continue;
      const expTimes =
        s.experience_times && typeof s.experience_times === "object"
          ? (s.experience_times as Record<string, number>)
          : {};

      const abaId = nonEmptyString((s as any).aba_id);
      const asaId = nonEmptyString((s as any).asa_id);
      const primaryAgent = Array.isArray(s.agent_id)
        ? s.agent_id.find(
            (x): x is string => typeof x === "string" && x.trim() !== "",
          )
        : null;
      const customerId = nonEmptyString(s.customer_id);

      for (const exp of visited) {
        let row = byExp.get(exp);
        if (!row) {
          row = {
            experience: exp,
            views: 0,
            timeSpent: 0,
            businessAdminIds: new Set<string>(),
            systemAdminIds: new Set<string>(),
            agentIds: new Set<string>(),
            customerIds: new Set<string>(),
            rawVisitorViews: 0,
          };
          byExp.set(exp, row);
        }
        row.views++;
        const t = expTimes[exp];
        if (typeof t === "number") row.timeSpent += t;

        if (abaId) row.businessAdminIds.add(abaId);
        else if (asaId) row.systemAdminIds.add(asaId);
        else if (primaryAgent) row.agentIds.add(primaryAgent);
        else if (customerId) row.customerIds.add(customerId);
        else row.rawVisitorViews++;
      }
    }
    return Array.from(byExp.values()).sort((a, b) => b.views - a.views);
  }, [projectSessions]);

  const toggleRole = (kind: UserProfileKind) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const toggleUser = (key: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resolveUserProfile = (
    s: VrSession,
  ): { kind: UserProfileKind; name: string | null } => {
    const abaId = (s as any).aba_id as string | null | undefined;
    if (abaId)
      return {
        kind: "business_admin",
        name: businessAdminById.get(String(abaId)) ?? null,
      };
    const asaId = (s as any).asa_id as string | null | undefined;
    if (asaId)
      return {
        kind: "system_admin",
        name: systemAdminById.get(String(asaId)) ?? null,
      };
    if (Array.isArray(s.agent_id)) {
      const id = s.agent_id.find(
        (x) => typeof x === "string" && x.trim() !== "",
      );
      if (id)
        return { kind: "agent", name: agentNameById.get(String(id)) ?? null };
    }
    if (s.customer_id)
      return {
        kind: "customer",
        name: customerNameById.get(String(s.customer_id)) ?? null,
      };
    return { kind: "raw_visitor", name: null };
  };

  const totalSessionsPages = Math.max(
    1,
    Math.ceil(filteredSessions.length / SESSIONS_PAGE_SIZE),
  );

  useEffect(() => {
    if (sessionsPage > totalSessionsPages) setSessionsPage(1);
  }, [filteredSessions, sessionsPage, totalSessionsPages]);

  const pagedSessions = useMemo(() => {
    const start = (sessionsPage - 1) * SESSIONS_PAGE_SIZE;
    return filteredSessions.slice(start, start + SESSIONS_PAGE_SIZE);
  }, [filteredSessions, sessionsPage]);

  const totalAgentsLifetime = allAgents.length;

  const summary = useMemo(() => {
    const totalViews = filteredSessions.length;

    const hasAgent = (s: VrSession) =>
      Array.isArray(s.agent_id) && s.agent_id.length > 0;

    const projectIds = new Set<string>();
    const agentIds = new Set<string>();
    const assignedCustomers = new Set<string>();
    const unassignedCustomers = new Set<string>();

    let mobileViews = 0;
    let desktopViews = 0;
    let vrViews = 0;
    let totalDuration = 0;

    for (const s of filteredSessions) {
      if (s.project_id) projectIds.add(s.project_id);
      if (Array.isArray(s.agent_id)) {
        for (const id of s.agent_id) if (id) agentIds.add(id);
      }
      if (s.customer_id) {
        if (hasAgent(s)) assignedCustomers.add(s.customer_id);
        else unassignedCustomers.add(s.customer_id);
      }
      if (s.device === "mobile") mobileViews++;
      else if (s.device === "desktop") desktopViews++;
      if (s.used_vr === true) vrViews++;
      if (typeof s.total_duration === "number") totalDuration += s.total_duration;
    }

    const avgDuration =
      totalViews > 0 ? Math.round(totalDuration / totalViews) : 0;

    return {
      totalProjects: projectIds.size,
      totalAgents: agentIds.size,
      unassignedLeads: unassignedCustomers.size,
      assignedLeads: assignedCustomers.size,
      totalViews,
      mobileViews,
      desktopViews,
      vrViews,
      totalDuration,
      avgDuration,
    };
  }, [filteredSessions]);

  const showData = scopeResolved && scope.agencyId && !isLoading && !error;

  if (showPrompt) {
    return (
      <DesktopModePrompt
        message="This page contains analytics dashboards and tables that are best viewed in desktop mode. Switch to desktop view for the best experience."
        onAccept={accept}
        onGoBack={() => {
          dismiss();
          navigate(slug ? `/c/${slug}` : "/");
        }}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-1 text-sm flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate(slug ? `/c/${slug}` : "/")}
          className="text-[#889DB2] hover:underline"
        >
          Home
        </button>
        <span className="text-[#889DB2] mx-1">›</span>
        <span className="text-white font-medium">Analytics</span>
      </div>
      <nav className="border-b flex items-end justify-between gap-3">
        <div className="flex gap-1">
          <TabButton
            active={activeTab === "overall"}
            onClick={() => setActiveTab("overall")}
          >
            Overall Analytics
          </TabButton>
          <TabButton
            active={activeTab === "project"}
            onClick={() => setActiveTab("project")}
          >
            Project Analytics
          </TabButton>
        </div>

        <div className="flex items-end gap-3 pb-2">
          <select
            value={datePreset}
            onChange={(e) => {
              const key = e.target.value;
              setDatePreset(key);
              const preset = DATE_PRESETS.find((p) => p.key === key);
              const { from, to } = computeDateRange(preset?.days ?? null);
              setFromDate(from);
              setToDate(to);
            }}
            disabled={isLoading || !scope.agencyId}
            className="rounded px-3 py-1.5 text-sm bg-[#2e4051] text-white disabled:opacity-50"
          >
            {DATE_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </nav>

      {activeTab === "project" && (
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-white">
              Select project
            </span>
            <div className="relative" ref={projectPickerRef}>
              <button
                type="button"
                onClick={() => setProjectPickerOpen((o) => !o)}
                className="border border-white rounded px-3 py-1.5 text-sm bg-[#2e4051] text-white min-w-[240px] text-left flex items-center justify-between gap-2"
              >
                <span>
                  {selectedProjectId
                    ? projectNameById.get(selectedProjectId) ?? selectedProjectId
                    : "Select a project…"}
                </span>
                <span className="text-gray-300">▾</span>
              </button>
              {projectPickerOpen && (
                <div className="absolute z-10 mt-1 w-full rounded bg-[#2e4051] text-white shadow max-h-72 overflow-auto">
                  {projectNameById.size === 0 ? (
                    <div className="p-3 text-sm text-gray-300">
                      No projects.
                    </div>
                  ) : (
                    Array.from(projectNameById.entries()).map(([id, name]) => (
                      <label
                        key={id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 cursor-pointer text-sm"
                      >
                        <input
                          type="radio"
                          name="project-picker"
                          checked={selectedProjectId === id}
                          onChange={() => {
                            setSelectedProjectId(id);
                            setProjectPickerOpen(false);
                          }}
                        />
                        <span>{name}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {!selectedProjectId && (
            <div className="text-sm text-gray-500 border rounded p-6 text-center">
              Pick a project to see its analytics.
            </div>
          )}

          {selectedProjectId && (
            <>
              <section className="bg-[#161F28] rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard
                  dark
                  label="Total Views"
                  value={projectSummary.totalViews}
                />
                <SummaryCard
                  dark
                  label="Mobile Views"
                  value={projectSummary.mobileViews}
                />
                <SummaryCard
                  dark
                  label="Desktop Views"
                  value={projectSummary.desktopViews}
                />
                <SummaryCard
                  dark
                  label="VR views"
                  value={projectSummary.vrViews}
                  sub={
                    projectSummary.totalViews > 0 &&
                    projectSummary.vrViews > 0
                      ? `${Math.round(
                          (projectSummary.vrViews /
                            projectSummary.totalViews) *
                            100,
                        )}%`
                      : undefined
                  }
                />
                <SummaryCard
                  dark
                  label="Total Time Spent"
                  value={formatDuration(projectSummary.totalDuration)}
                />
                <SummaryCard
                  dark
                  label="Average time per view"
                  value={formatDuration(projectSummary.avgDuration)}
                />
              </section>

              <nav className="border-b flex gap-1">
                <TabButton
                  active={projectSubTab === "user_profile"}
                  onClick={() => setProjectSubTab("user_profile")}
                >
                  By User Profile
                </TabButton>
                <TabButton
                  active={projectSubTab === "location"}
                  onClick={() => setProjectSubTab("location")}
                >
                  By Location
                </TabButton>
                <TabButton
                  active={projectSubTab === "experience"}
                  onClick={() => setProjectSubTab("experience")}
                >
                  By Experience Zone
                </TabButton>
              </nav>

              {projectSubTab === "user_profile" && (
                <div className="overflow-auto rounded">
                  <table className="min-w-full text-sm [&_tbody_tr]:border-b [&_tbody_tr]:border-[#161F28] [&_td]:border-0">
                    <thead className="bg-[#161F28] text-white">
                      <tr>
                        <Th>User Role</Th>
                        <Th>Name</Th>
                        <Th>Phone Number</Th>
                        <Th>Experiences</Th>
                        <Th>Visitors</Th>
                        <Th>Views</Th>
                        <Th>Time Spent</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        isAgent
                          ? (["agent", "customer"] as UserProfileKind[])
                          : ([
                              "business_admin",
                              "system_admin",
                              "agent",
                              "customer",
                              "raw_visitor",
                            ] as UserProfileKind[])
                      ).map((kind) => {
                        const bucket = projectUserBreakdown[kind];
                        const rows = Array.from(bucket.users.values());
                        const isOpen = expandedRoles.has(kind);
                        const agentDirectView = isAgent && kind === "agent";
                        const expandable = kind !== "raw_visitor" && !agentDirectView;
                        const agentExpKey = "agent_direct_experiences";
                        const agentExpOpen =
                          agentDirectView && expandedUsers.has(agentExpKey);
                        return (
                          <React.Fragment key={kind}>
                            <tr className="bg-[#2e4051] text-white">
                              <Td>
                                <span className="inline-flex items-center gap-2">
                                  {expandable ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleRole(kind)}
                                      className="w-5 h-5 rounded border flex items-center justify-center text-xs font-bold text-gray-700 bg-gray-50 hover:bg-gray-100"
                                      aria-label={
                                        isOpen ? "Collapse" : "Expand"
                                      }
                                    >
                                      {isOpen ? "−" : "+"}
                                    </button>
                                  ) : (
                                    <span className="w-5 h-5 inline-block" />
                                  )}
                                  <span
                                    className={
                                      "inline-block px-2 py-0.5 rounded text-xs " +
                                      USER_PROFILE_STYLE[kind]
                                    }
                                  >
                                    {isAgent && kind === "agent"
                                      ? "My View"
                                      : isAgent && kind === "customer"
                                      ? "My Leads"
                                      : USER_PROFILE_LABEL[kind]}
                                  </span>
                                </span>
                              </Td>
                              <Td>—</Td>
                              <Td>—</Td>
                              <Td>
                                {agentDirectView &&
                                bucket.experiences.size > 0 ? (
                                  <span className="inline-flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => toggleUser(agentExpKey)}
                                      className="w-5 h-5 rounded border flex items-center justify-center text-xs font-bold text-gray-700 bg-gray-50 hover:bg-gray-100"
                                      aria-label={
                                        agentExpOpen ? "Collapse" : "Expand"
                                      }
                                    >
                                      {agentExpOpen ? "−" : "+"}
                                    </button>
                                    <span>{bucket.experiences.size}</span>
                                  </span>
                                ) : kind === "raw_visitor" ? (
                                  "—"
                                ) : (
                                  bucket.experiences.size || "—"
                                )}
                              </Td>
                              <Td>
                                {kind === "raw_visitor"
                                  ? bucket.views || "—"
                                  : bucket.users.size || "—"}
                              </Td>
                              <Td>{bucket.views || "—"}</Td>
                              <Td>
                                {bucket.timeSpent > 0
                                  ? formatDuration(bucket.timeSpent)
                                  : "—"}
                              </Td>
                            </tr>
                            {agentDirectView &&
                              agentExpOpen &&
                              Array.from(bucket.experienceStats.entries()).map(
                                ([expName, stat]) => (
                                  <tr
                                    key={`agent-direct-exp-${expName}`}
                                    className="bg-[#2e4051] text-white"
                                  >
                                    <Td>
                                      <span className="pl-7 text-xs text-gray-300">
                                        ↳
                                      </span>
                                    </Td>
                                    <Td>—</Td>
                                    <Td>—</Td>
                                    <Td>
                                      <span
                                        className={
                                          "inline-block px-2 py-0.5 rounded text-xs " +
                                          experienceColor(expName)
                                        }
                                      >
                                        {expName}
                                      </span>
                                    </Td>
                                    <Td>—</Td>
                                    <Td>{stat.views}</Td>
                                    <Td>{formatDuration(stat.timeSpent)}</Td>
                                  </tr>
                                ),
                              )}
                            {expandable && isOpen && rows.length === 0 && (
                              <tr className="bg-[#2e4051] text-white">
                                <Td>
                                  <span className="pl-7 text-gray-300">
                                    null
                                  </span>
                                </Td>
                                <Td>—</Td>
                                <Td>—</Td>
                                <Td>—</Td>
                                <Td>—</Td>
                                <Td>—</Td>
                                <Td>—</Td>
                              </tr>
                            )}
                            {expandable &&
                              isOpen &&
                              rows.map((r) => {
                                const userKey = `${kind}-${r.id}`;
                                const userOpen = expandedUsers.has(userKey);
                                const expRows = Array.from(
                                  r.experienceStats.entries(),
                                );
                                return (
                                  <React.Fragment key={userKey}>
                                    <tr className="bg-[#2e4051] text-white">
                                      <Td>
                                        <span className="pl-7 text-xs text-gray-300">
                                          ↳
                                        </span>
                                      </Td>
                                      <Td>{r.name}</Td>
                                      <Td>{r.phone}</Td>
                                      <Td>
                                        <span className="inline-flex items-center gap-2">
                                          {r.experiences.size > 0 ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                toggleUser(userKey)
                                              }
                                              className="w-5 h-5 rounded border flex items-center justify-center text-xs font-bold text-gray-700 bg-white hover:bg-gray-100"
                                              aria-label={
                                                userOpen
                                                  ? "Collapse"
                                                  : "Expand"
                                              }
                                            >
                                              {userOpen ? "−" : "+"}
                                            </button>
                                          ) : (
                                            <span className="w-5 h-5 inline-block" />
                                          )}
                                          <span>
                                            {r.experiences.size || "—"}
                                          </span>
                                        </span>
                                      </Td>
                                      <Td>1</Td>
                                      <Td>{r.views}</Td>
                                      <Td>{formatDuration(r.timeSpent)}</Td>
                                    </tr>
                                    {userOpen &&
                                      expRows.map(([expName, stat]) => (
                                        <tr
                                          key={`${userKey}-${expName}`}
                                          className="bg-[#2e4051] text-white"
                                        >
                                          <Td>
                                            <span className="pl-14 text-xs text-gray-300">
                                              ↳
                                            </span>
                                          </Td>
                                          <Td>—</Td>
                                          <Td>—</Td>
                                          <Td>
                                            <span
                                              className={
                                                "inline-block px-2 py-0.5 rounded text-xs " +
                                                experienceColor(expName)
                                              }
                                            >
                                              {expName}
                                            </span>
                                          </Td>
                                          <Td>—</Td>
                                          <Td>{stat.views}</Td>
                                          <Td>
                                            {formatDuration(stat.timeSpent)}
                                          </Td>
                                        </tr>
                                      ))}
                                  </React.Fragment>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {projectSubTab === "location" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div
                    className="overflow-auto rounded"
                    style={{ maxHeight: 360 }}
                  >
                    <table className="min-w-full text-sm [&_tbody_tr]:border-b [&_tbody_tr]:border-[#161F28] [&_td]:border-0">
                    <thead className="bg-[#161F28] text-white">
                      <tr>
                        <Th>City</Th>
                        <Th>Total Visitors</Th>
                        <Th>Total Views</Th>
                        <Th>Total Time Spent</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectLocationBreakdown.length === 0 ? (
                        <tr className="bg-[#2e4051]">
                          <td
                            colSpan={4}
                            className="px-3 py-6 text-center text-gray-300"
                          >
                            No location data.
                          </td>
                        </tr>
                      ) : (
                        projectLocationBreakdown.map((r) => {
                          const visitors =
                            r.businessAdminIds.size +
                            r.systemAdminIds.size +
                            r.agentIds.size +
                            r.customerIds.size +
                            r.rawVisitorViews;
                          return (
                            <tr
                              key={r.city}
                              className="bg-[#2e4051] text-white"
                            >
                              <Td>{r.city}</Td>
                              <Td>{visitors}</Td>
                              <Td>{r.views}</Td>
                              <Td>{formatDuration(r.timeSpent)}</Td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  </div>
                  <LocationsMap
                    cityRows={projectLocationBreakdown
                      .filter((r) => r.city && r.city !== "Unknown")
                      .map((r) => ({
                        city: r.city,
                        visitors:
                          r.businessAdminIds.size +
                          r.systemAdminIds.size +
                          r.agentIds.size +
                          r.customerIds.size +
                          r.rawVisitorViews,
                        views: r.views,
                        timeSpent: r.timeSpent,
                      }))}
                  />
                </div>
              )}

              {projectSubTab === "experience" && (
                <div className="overflow-auto rounded">
                  <table className="min-w-full text-sm [&_tbody_tr]:border-b [&_tbody_tr]:border-[#161F28] [&_td]:border-0">
                    <thead className="bg-[#161F28] text-white">
                      <tr>
                        <Th>Experiences</Th>
                        <Th>Visitors</Th>
                        <Th>Views</Th>
                        <Th>Time Spent</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectExperienceBreakdown.length === 0 ? (
                        <tr className="bg-[#2e4051]">
                          <td
                            colSpan={4}
                            className="px-3 py-6 text-center text-gray-300"
                          >
                            No experiences visited.
                          </td>
                        </tr>
                      ) : (
                        projectExperienceBreakdown.map((r) => {
                          const visitors =
                            r.businessAdminIds.size +
                            r.systemAdminIds.size +
                            r.agentIds.size +
                            r.customerIds.size +
                            r.rawVisitorViews;
                          return (
                            <tr
                              key={r.experience}
                              className="bg-[#2e4051] text-white"
                            >
                              <Td>
                                <span
                                  className={
                                    "inline-block px-2 py-0.5 rounded text-xs " +
                                    experienceColor(r.experience)
                                  }
                                >
                                  {r.experience}
                                </span>
                              </Td>
                              <Td>{visitors}</Td>
                              <Td>{r.views}</Td>
                              <Td>{formatDuration(r.timeSpent)}</Td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === "overall" && showData && (
        <section className="bg-[#161F28] rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <SummaryCard dark label="Total Projects" value={totalProjects} />
          {!isAgent && (
            <SummaryCard
              dark
              label="Total Agents"
              value={totalAgentsLifetime}
            />
          )}
          {!isAgent && (
            <SummaryCard
              dark
              label="Total Unassigned Leads"
              value={unassignedLeads}
            />
          )}
          <SummaryCard
            dark
            label={isAgent ? "Total Leads" : "Total Assigned Leads"}
            value={isAgent ? agentLeadCount : assignedLeads}
          />
          <SummaryCard dark label="Total Views" value={summary.totalViews} />
          <SummaryCard dark label="Mobile views" value={summary.mobileViews} />
          <SummaryCard
            dark
            label="Desktop views"
            value={summary.desktopViews}
          />
          <SummaryCard
            dark
            label="VR views"
            value={summary.vrViews}
            sub={
              summary.totalViews > 0 && summary.vrViews > 0
                ? `${Math.round(
                    (summary.vrViews / summary.totalViews) * 100,
                  )}%`
                : undefined
            }
          />
          <SummaryCard
            dark
            label="Total Time Spent"
            value={formatDuration(summary.totalDuration)}
          />
          <SummaryCard
            dark
            label="Average time per view"
            value={formatDuration(summary.avgDuration)}
          />
        </section>
      )}

      {!scopeResolved && (
        <div className="text-sm text-gray-500">Resolving user scope…</div>
      )}

      {scopeResolved && !scope.agencyId && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          No agency could be resolved for your account.
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {activeTab === "overall" && showData && (
        <>

          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-white">
                Showing {filteredSessions.length} session
                {filteredSessions.length === 1 ? "" : "s"}
              </div>
              <button
                type="button"
                onClick={() => downloadSessionsCsv(sessions, resolveUserProfile, projectNameById)}
                disabled={sessions.length === 0}
                className="text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded inline-flex items-center gap-2"
              >
                Download Excel
              </button>
            </div>
            {filteredSessions.length === 0 ? (
              <div className="text-sm text-gray-300 rounded p-6 text-center bg-[#2e4051]">
                No sessions available in selected time range
              </div>
            ) : (
              <div className="overflow-auto rounded">
                <table className="min-w-full text-sm [&_td]:border-0">
                  <thead className="bg-[#161F28] text-white">
                    <tr>
                      <Th>Session</Th>
                      <Th>Date</Th>
                      <Th>User Profile</Th>
                      <Th>Project</Th>
                      <Th>Device</Th>
                      <Th>Location</Th>
                      <Th>Duration</Th>
                      <Th>VR</Th>
                      <Th>Most viewed zone</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSessions.map((s) => (
                      <tr key={s._id} className="bg-[#2e4051] text-white">
                        <Td>{s.session_id ?? "—"}</Td>
                        <Td>{formatDateTime(s.created_at)}</Td>
                        <Td>
                          {(() => {
                            const { kind, name } = resolveUserProfile(s);
                            return (
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className={
                                    "inline-block px-2 py-0.5 rounded text-xs " +
                                    USER_PROFILE_STYLE[kind]
                                  }
                                >
                                  {USER_PROFILE_LABEL[kind]}
                                </span>
                                {name && (
                                  <span className="text-sm">{name}</span>
                                )}
                              </span>
                            );
                          })()}
                        </Td>
                        <Td>
                          {s.project_id
                            ? projectNameById.get(s.project_id) ?? "—"
                            : "—"}
                        </Td>
                        <Td>{s.device ?? "—"}</Td>
                        <Td>{formatLocation(s)}</Td>
                        <Td>{formatDuration(s.total_duration)}</Td>
                        <Td>
                          {s.used_vr === true ? (
                            <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">
                              VR
                            </span>
                          ) : s.used_vr === false ? (
                            <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                              2D
                            </span>
                          ) : (
                            "—"
                          )}
                        </Td>
                        <Td>{s.most_viewed_zone ?? "—"}</Td>
                        <Td>
                          <span
                            className={
                              s.status === "completed"
                                ? "inline-block px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs"
                                : "inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs"
                            }
                          >
                            {s.status ?? "—"}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {sessions.length > SESSIONS_PAGE_SIZE && (
              <div className="flex items-center justify-end gap-2 mt-3 text-sm text-white">
                <button
                  type="button"
                  onClick={() =>
                    setSessionsPage((p) => Math.max(1, p - 1))
                  }
                  disabled={sessionsPage === 1}
                  className="px-3 py-1 rounded bg-[#2e4051] disabled:opacity-50"
                >
                  Prev
                </button>
                <span>
                  Page {sessionsPage} of {totalSessionsPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setSessionsPage((p) =>
                      Math.min(totalSessionsPages, p + 1),
                    )
                  }
                  disabled={sessionsPage === totalSessionsPages}
                  className="px-3 py-1 rounded bg-[#2e4051] disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

type CityMarker = {
  city: string;
  visitors: number;
  views: number;
  timeSpent: number;
};

const LocationsMap = ({ cityRows }: { cityRows: CityMarker[] }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [coords, setCoords] = useState<Map<string, { lat: number; lng: number }>>(
    new Map(),
  );
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (!apiKey) return undefined;
    let cancelled = false;
    (async () => {
      const next = new Map(coords);
      let changed = false;
      for (const row of cityRows) {
        if (!row.city || row.city === "Unknown") continue;
        if (next.has(row.city)) continue;
        try {
          const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            row.city,
          )}&key=${apiKey}`;
          const res = await fetch(url);
          const data = await res.json();
          if (cancelled) return;
          const loc = data?.results?.[0]?.geometry?.location;
          if (data?.status === "OK" && loc) {
            next.set(row.city, { lat: loc.lat, lng: loc.lng });
            changed = true;
          }
        } catch {
          // ignore individual failures
        }
      }
      if (changed && !cancelled) setCoords(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityRows, apiKey]);

  const markers = useMemo(
    () =>
      cityRows
        .map((r) => ({ row: r, pos: coords.get(r.city) }))
        .filter(
          (x): x is { row: CityMarker; pos: { lat: number; lng: number } } =>
            !!x.pos,
        ),
    [cityRows, coords],
  );

  if (!apiKey) {
    return (
      <div className="text-sm text-gray-300 bg-[#2e4051] rounded p-4">
        Google Maps API key not configured.
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey}>
      <div
        className="rounded overflow-hidden"
        style={{ height: 360, width: "100%" }}
      >
        <GoogleMap
          mapId="client-analytics-v2-locations"
          defaultCenter={{ lat: 20.5937, lng: 78.9629 }}
          defaultZoom={5}
          gestureHandling="greedy"
          disableDefaultUI={false}
        >
          {markers.map(({ row, pos }) => (
            <AdvancedMarker
              key={row.city}
              position={pos}
              onClick={() => setActive(row.city)}
            >
              <Pin
                background="#2e4051"
                borderColor="#161F28"
                glyphColor="#ffffff"
              />
            </AdvancedMarker>
          ))}
          {active &&
            (() => {
              const m = markers.find((x) => x.row.city === active);
              if (!m) return null;
              return (
                <InfoWindow
                  position={m.pos}
                  onCloseClick={() => setActive(null)}
                >
                  <div className="text-sm">
                    <div className="font-semibold">{m.row.city}</div>
                    <div>Visitors: {m.row.visitors}</div>
                    <div>Views: {m.row.views}</div>
                  </div>
                </InfoWindow>
              );
            })()}
        </GoogleMap>
      </div>
    </APIProvider>
  );
};

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={
      "px-4 py-2 text-sm font-medium border-b-2 -mb-px " +
      (active
        ? "border-white text-white"
        : "border-transparent text-[#889DB2] hover:text-white")
    }
  >
    {children}
  </button>
);

const SummaryCard = ({
  label,
  value,
  sub,
  dark = false,
}: {
  label: string;
  value: number | string | null | undefined;
  sub?: string;
  dark?: boolean;
}) => {
  const isLoading = value === null || value === undefined;
  const spinner = (
    <span
      className={
        "inline-block w-4 h-4 rounded-full border-2 animate-spin " +
        (dark
          ? "border-white/20 border-t-white"
          : "border-gray-300 border-t-gray-700")
      }
    />
  );
  return dark ? (
    <div className="rounded p-3 bg-[#2e4051]">
      <div className="text-xs text-gray-300">{label}</div>
      <div className="text-xl font-semibold mt-1 text-white">
        {isLoading ? spinner : value}
      </div>
      {sub && <div className="text-xs text-gray-300 mt-0.5">{sub}</div>}
    </div>
  ) : (
    <div className="border rounded p-3 bg-white">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold mt-1">
        {isLoading ? spinner : value}
      </div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const Th = ({ children }: { children: React.ReactNode }) => (
  <th className="text-left font-medium px-3 py-2 border-b whitespace-nowrap">
    {children}
  </th>
);

const Td = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <td className={`px-3 py-2 border-b align-top ${className}`}>{children}</td>
);

export { ClientAnalyticsV2 };
export default ClientAnalyticsV2;
